
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { debounce } from "lodash";
import { firstValueFrom } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, RaskRoller, VirkesorderStatus } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { AvslutaVirkesorderCommand } from "../../commands/AvslutaVirkesorderCommand";
import { ArbetsorderMonitor, ArbetsorderMonitorService } from "../../services/arbetsorder-monitor.service";

@Component({
    selector: "rask-avsluta-virkesorder-button",
    templateUrl: "./avsluta-virkesorder-button.component.html",
    styleUrls: ["./avsluta-virkesorder-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ButtonComponent]
})
export class AvslutaVirkesorderButtonComponent implements OnInit, OnDestroy {

    @Input() arbetsorder: IArbetsorderDetail;

    isSendInProgress = false;
    isSynchronizationInProgress = false;
    workMonitor: ArbetsorderMonitor;

    private syncDebouncer: any;
    private sendDebouncer: any;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly arbetsorderMonitorService = inject(ArbetsorderMonitorService);
    private readonly commandService = inject(CommandService);

    get canAvsluta(): boolean {
        if (!this.authenticationService.hasRoles(RaskRoller.role.Produktionsleda)) {
            return false;
        }

        if (this.isSendInProgress) {
            return false;
        }

        if (this.isSynchronizationInProgress) {
            return false;
        }

        return this.arbetsorder.virkesorderStatus
            .some(x => x.status === VirkesorderStatus.Aktiv || x.status === VirkesorderStatus.FelLoadTender);
    }

    ngOnInit(): void {
        this.workMonitor = this.arbetsorderMonitorService.connect();
        this.workMonitor.getWork("viol2.spegla").subscribe(x => {
            const bounceTime = x ? 0 : 1000;
            this.doSyncDebounce(bounceTime, x);
        });
    }

    ngOnDestroy(): void {
        this.workMonitor.disconnect();
    }

    avsluta() {
        let message = "Virkesordern kommer att avslutas och ett nytt utkast skapas. Eventuella ändringar till virkesordern kommer att skrivas över. Tryck Avsluta virkesorder eller Avbryt";
        if (!this.arbetsorder.raskVo) {
            message = "Vals virkesordern kommer att avslutas och ett nytt utkast i rask skapas. Eventuella ändringar till virkesordern kommer att skrivas över. Tryck Avsluta virkesorder eller Avbryt";
        }
        const settings = new ModalSettings("Avsluta virkesorder", message);
        settings.yesCaption = "Avsluta virkesorder";
        settings.noCaption = "Avbryt";
        firstValueFrom(this.raskModalService.confirm(settings))
            .then((response) => {
                if (response === ConfirmDialogResponse.save) {
                    this.doSendDebounce(0, true);
                    const command = new AvslutaVirkesorderCommand((x) => {
                        x.arbetsorderId = this.arbetsorder.id;
                    });

                    // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
                    const local = this.commandService.createInstance();
                    local.addTransactionCommand(command);

                    this.busyService
                        .addWork(local.pushCommandQueueTransaction())
                        .subscribe(x => {
                            this.doSendDebounce(2000, false);

                            if (!this.arbetsorder.raskVo) {
                                window.location.reload();
                            }
                        });
                }
            });
    }

    private doSyncDebounce(bounceTime: number, isSynchronizationInProgress: boolean): void {
        if (this.syncDebouncer) {
            this.syncDebouncer.cancel();
        }

        this.syncDebouncer = debounce(() => {
            this.isSynchronizationInProgress = isSynchronizationInProgress;
            this.syncDebouncer = undefined;
        }, bounceTime);

        this.syncDebouncer();
    }

    private doSendDebounce(bounceTime: number, isSendInProgress: boolean): void {
        if (this.sendDebouncer) {
            this.sendDebouncer.cancel();
        }

        this.sendDebouncer = debounce(() => {
            this.isSendInProgress = isSendInProgress;
            this.sendDebouncer = undefined;
        }, bounceTime);

        this.sendDebouncer();
    }
}
